import { Stack, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import apiRoutes from "../../../../../constants/api-routes";
import messageLevels from "../../../../../constants/message-levels";
import messages from "../../../../../constants/messages";
import useAuthHeader from "../../../../../hooks/useAuthHeader";
import useToastAlert from "../../../../../hooks/useToastAlert";
import { useScimWizardContext } from "../../../../../providers/ScimWizardProvider";
import { selectOrganizationId } from "../../../../../redux/userSlice";
import CoAppCreateGroupOrRoleModal from "../../../../global/components/modals/CoAppCreateGroupOrRoleModal";
import CoAppDestructiveConfirmationModal from "../../../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppEditNameModal from "../../../../global/components/modals/CoAppEditNameModal";
import { CoAppInvertedActionButton } from "../../../../global/styled/global.styled";
import { getStep5RoleColumns } from "../columns/step5-role-columns";
import ScimWizardDataGrid from "../ScimWizardDataGrid";
import { ScimWizardDataGridStepContainer, ScimWizardDataGridWrapper } from "../styled/scim-wizard.styled";

export default function Step5() {
	const { identityProviderName, mappedRoles, setMappedRoles, validateRoleMappings, roleMappingStatus } = useScimWizardContext();

	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const organizationId = useSelector(selectOrganizationId);
	const apiRef = useGridApiRef();
	const [roles, setRoles] = useState([]);
	const [idpGroups, setIdpGroups] = useState([]);
	const [roleName, setRoleName] = useState("");
	const [roleNameError, setRoleNameError] = useState();
	const [createRoleDialogOpen, setCreateRoleDialogOpen] = useState(false);
	const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
	const [roleBeingEdited, setRoleBeingEdited] = useState({});
	const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
	const [roleBeingDeleted, setRoleBeingDeleted] = useState({});

	const toggleCreateRoleDialog = () => {
		setCreateRoleDialogOpen(!createRoleDialogOpen);
		setRoleNameError(undefined);
		setRoleName("");
	};

	const toggleEditRoleDialog = (role = {}) => {
		setEditRoleDialogOpen(!editRoleDialogOpen);
		setRoleNameError(undefined);
		setRoleName("");
		setRoleBeingEdited(role);
	};

	const toggleDeleteRoleDialog = (role = {}) => {
		setDeleteRoleDialogOpen(!deleteRoleDialogOpen);
		setRoleBeingDeleted(role);
	};

	const handleRoleNameChange = (e: any) => {
		setRoleName(e.target.value);
		setRoleNameError(undefined);
	};

	const createNewRole = () => {
		if (!roleName) {
			setRoleNameError("Required *" as any);
			return;
		}

		const role = { name: roleName };

		axios.post(apiRoutes.createRole, role, { headers: authHeader })
			.then(() => {
				toggleCreateRoleDialog();
				initializeOrganizationRoles();
			})
			.catch((err) => {
				if (err.response.data.message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_ALREADY_EXISTS_ERROR_MSG("Role") as any);
					return;
				}
				toggleCreateRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_CREATION_ERROR_MSG);
			});
	};

	const handleRoleNameChangeSubmit = (e: any) => {
		if (!roleName) return;

		if (roleName === (roleBeingEdited as any).name) {
			toggleEditRoleDialog();
			return;
		}

		axios.put(apiRoutes.updateRole((roleBeingEdited as any).id), { name: roleName }, { headers: authHeader })
			.then(() => {
				initializeOrganizationRoles();
				toggleEditRoleDialog();
			})
			.catch((e) => {
				console.log(e);
				if (e.response.data.message.includes("already exists")) {
					setRoleNameError(messages.RESOURCE_NAME_ALREADY_EXISTS_ERROR_MSG("Role") as any);
					return;
				}
				toggleEditRoleDialog();
				handleToastAlert(messageLevels.ERROR, messages.ROLE_NAME_UPDATE_ERROR_MSG);
			});
	};
	const deleteRole = () => {
		axios.delete(apiRoutes.deleteRole((roleBeingDeleted as any).id), { headers: authHeader })
			.then(() => {
				initializeOrganizationRoles();
				toggleDeleteRoleDialog();
			})
			.catch((err) => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.ROLE_DELETION_ERROR_MSG);
				toggleDeleteRoleDialog();
			});
	};

	const initializeOrganizationRoles = () => {
		axios.get(apiRoutes.getRoles, { headers: authHeader })
			.then((res) => {
				console.log(res.data)
				const superAdminRole = res.data.find((role: any) => role.isProtected);
				if (superAdminRole) {
					const superAdminIndex = res.data.indexOf(superAdminRole);
					res.data.splice(superAdminIndex, 1);
					res.data.unshift(superAdminRole);
				}
				setRoles(res.data);
				const tempMappedRoles = { ...mappedRoles };
				for (const role of res.data) {
					if (Object.keys(roleBeingEdited as any).length > 0 && role.id === (roleBeingEdited as any).id) {
						tempMappedRoles[`${role.name}`] = tempMappedRoles[`${(roleBeingEdited as any).name}`];
					} else {
						tempMappedRoles[`${role.name}`] = !tempMappedRoles[`${role.name}`] ? [] : tempMappedRoles[`${role.name}`];
					}
				}
				if (Object.keys(roleBeingDeleted).length > 0) {
					delete tempMappedRoles[`${(roleBeingDeleted as any).name}`];
				}
				if (Object.keys(roleBeingEdited as any).length > 0) {
					delete tempMappedRoles[`${(roleBeingEdited as any).name}`];
				}
				setMappedRoles(tempMappedRoles);
			})
			.catch(err => {
				console.log(err);
			});
	};

	const initializeExternalGroups = async () => {
		axios.get(apiRoutes.getExternalGroups(organizationId as unknown as string), {
			headers: authHeader
		})
			.then((res) => {
				setIdpGroups(res.data);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const columns = useMemo(() => (
		getStep5RoleColumns(
			identityProviderName,
			idpGroups,
			mappedRoles as any,
			setMappedRoles as any,
			apiRef,
			validateRoleMappings as any,
			roleMappingStatus,
			initializeExternalGroups,
			setIdpGroups as any,
			toggleEditRoleDialog as any,
			toggleDeleteRoleDialog as any
		)
	), [identityProviderName, idpGroups, mappedRoles, apiRef, roleMappingStatus]);

	useEffect(() => {
		initializeOrganizationRoles();
	}, []);

	return (
		<ScimWizardDataGridStepContainer>
			<Stack direction="row" spacing={10} sx={{ paddingLeft: "55px" }}>
				<Typography variant="h5">Map Roles</Typography>
				<CoAppInvertedActionButton onClick={toggleCreateRoleDialog}>
					Add Role
				</CoAppInvertedActionButton>
			</Stack>
			<ScimWizardDataGridWrapper>
				<ScimWizardDataGrid
					apiRef={apiRef}
					rows={roles}
					columns={columns}
				/>
			</ScimWizardDataGridWrapper>
			<CoAppCreateGroupOrRoleModal
				cancelClickHandler={toggleCreateRoleDialog}
				changeHandler={handleRoleNameChange}
				dialogOpen={createRoleDialogOpen}
				nameError={roleNameError}
				confirmClickHandler={createNewRole}
				isRole={true}
			/>
			<CoAppEditNameModal
				dialogOpen={editRoleDialogOpen}
				dialogTitle="Edit role name"
				changeHandler={handleRoleNameChange}
				placeholderText={(roleBeingEdited as any).name}
				confirmClickHandler={handleRoleNameChangeSubmit}
				cancelClickHandler={toggleEditRoleDialog}
				actionText="Save"
				editNameError={roleNameError}
				setEditNameError={setRoleNameError}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={deleteRoleDialogOpen}
				dialogTitle={`Delete ${(roleBeingDeleted as any).name} role?`}
				dialogMessage={messages.DELETION_CONFIRMATION_MSG((roleBeingDeleted as any).name, "role")}
				confirmClickHandler={deleteRole}
				cancelClickHandler={toggleDeleteRoleDialog}
				actionText="Delete"
			/>
		</ScimWizardDataGridStepContainer>
	);
}